import axios, { type AxiosInstance } from 'axios';
import { action, computed, flow, makeObservable, observable, runInAction } from 'mobx';

import { depthSortedParameters, timeSortedParametersNoAutoRecognized } from '../../../shared/baseParams';
import { EventEmitter } from '../../../shared/EventEmitter';
import type { IDirectoriesService, TDictObject } from '../../../shared/IDirectoriesService';
import type { TUserData } from '../../../shared/types';
import { DRILLING_LOADS_MNEMONICS } from '../../constants';
import type {
  TAnomalyContextDto,
  TRecommendationDataType,
  TRecommendationRequest,
  TRecommendationResponse,
  TRecommendationRowData,
  TSourceData,
} from '../types';

import { Recommendation, type TData } from './Recommendation.entity';
import { RecommendationsApi } from './RecommendationsApi';
import { RecommendationsUiState } from './RecommendationsUiState';
import { RecommendationsValidationService } from './RecommendationsValidationService';

type TEventMap = {
  newRecommendations: Recommendation[];
  updateRecommendations: Recommendation[];
  deleteRecommendations: string[];
};

const mapSourceDataToRow = (source?: TSourceData): Pick<TRecommendationRowData, 'dataType' | 'paramValue'> => {
  if (!source) return {};
  switch (source.type) {
    case 'GENERAL_PARAMETERS':
      return { dataType: source.type, paramValue: source.param };
    case 'AUTOSUPERVISOR_OPERATIONS':
    case 'AUTOSUPERVISOR_KPI_STATES':
      return { dataType: source.type, paramValue: source.code };
    case 'DRILLING_LOADS':
      return { dataType: source.type, paramValue: source.mnemonic };
    // case 'LOAD_TRENDS':
    // case 'DRILLING_PERFORMANCE':
    //   return { dataType: source.type, paramValue: source.mnemonic };
    // case 'OPERATION_PARAMETERS':
    //   return { dataType: source.type, paramValue: source.code };
    default:
      return {};
  }
};

const mapRowToSourceData = (
  dataType?: TRecommendationDataType,
  paramValue?: string | number,
): TSourceData | undefined => {
  if (!dataType || paramValue == null) return undefined;
  switch (dataType) {
    case 'GENERAL_PARAMETERS':
      return { type: dataType, param: String(paramValue) };
    case 'AUTOSUPERVISOR_OPERATIONS':
    case 'AUTOSUPERVISOR_KPI_STATES':
      return { type: dataType, code: Number(paramValue) };
    case 'DRILLING_LOADS':
      return { type: dataType, mnemonic: String(paramValue) };
    // case 'LOAD_TRENDS':
    // case 'DRILLING_PERFORMANCE':
    //   return { type: dataType, mnemonic: String(paramValue) };
    // case 'OPERATION_PARAMETERS':
    //   return { type: dataType, code: String(paramValue) };
  }
};

const mapResponseToRow = (dto: TRecommendationResponse): TRecommendationRowData => {
  const ctx = dto.context ?? {};
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    blockIds: dto.blockIds ?? [],
    fieldIds: dto.fieldIds ?? [],
    padIds: dto.padIds ?? [],
    indexFormat: dto.indexFormat,
    recommendationMin: dto.recommendationMin,
    recommendationMax: dto.recommendationMax,
    recommendationDelta: dto.recommendationDelta,
    stageIds: ctx.stageIds ?? [],
    wellboreTypeIds: ctx.wellboreTypeIds ?? [],
    trajectoryTypeIds: ctx.trajectoryTypeIds ?? [],
    sectionTypeIds: ctx.sectionTypeIds ?? [],
    holeTypes: ctx.holeTypes ?? [],
    stratUnitTypeIds: ctx.stratUnitTypeIds ?? [],
    reservoirIds: ctx.reservoirIds ?? [],
    runTypeIds: ctx.runTypeIds ?? [],
    operationIds: ctx.operationIds ?? [],
    createdAt: dto.createdAt,
    createdBy: dto.createdBy,
    updatedAt: dto.updatedAt,
    updatedBy: dto.updatedBy,
    ...mapSourceDataToRow(dto.sourceData),
  };
};

const getCreatedAtTime = (recommendation: TRecommendationResponse): number => {
  const createdAtTime = recommendation.updatedAt ? new Date(recommendation.updatedAt).getTime() : NaN;

  return Number.isNaN(createdAtTime) ? 0 : createdAtTime;
};

const serializeRecommendation = (row: Recommendation): TRecommendationRequest => {
  const d = row.data;
  const context: TAnomalyContextDto = {
    stageIds: d.stageIds ?? [],
    wellboreTypeIds: d.wellboreTypeIds ?? [],
    trajectoryTypeIds: d.trajectoryTypeIds ?? [],
    sectionTypeIds: d.sectionTypeIds ?? [],
    holeTypes: d.holeTypes ?? [],
    stratUnitTypeIds: d.stratUnitTypeIds ?? [],
    reservoirIds: d.reservoirIds ?? [],
    runTypeIds: d.runTypeIds ?? [],
    operationIds: d.operationIds ?? [],
  };

  return {
    name: d.name ?? undefined,
    description: d.description ?? undefined,
    blockIds: d.blockIds ?? [],
    fieldIds: d.fieldIds ?? [],
    padIds: d.padIds ?? [],
    indexFormat: d.indexFormat ?? undefined,
    recommendationMin: d.recommendationMin ?? undefined,
    recommendationMax: d.recommendationMax ?? undefined,
    recommendationDelta: d.recommendationDelta ?? undefined,
    sourceData: mapRowToSourceData(d.dataType ?? undefined, d.paramValue ?? undefined),
    context,
  };
};

export class RecommendationsService {
  private readonly api: RecommendationsApi;
  private readonly abortControllers = new Map<string, AbortController>();

  readonly validationService = new RecommendationsValidationService();
  readonly eventEmitter = new EventEmitter<TEventMap>();
  readonly uiState = new RecommendationsUiState();

  @observable loading = false;
  @observable directoriesLoaded = false;

  @observable.ref blocks: TDictObject[] = [];
  @observable.ref fields: TDictObject[] = [];
  @observable.ref pads: TDictObject[] = [];
  @observable.ref autosupervisorOperations: TDictObject[] = [];
  @observable.ref autosupervisorKPIstates: TDictObject[] = [];
  @observable.ref workClassifierStageTypes: TDictObject[] = [];
  @observable.ref wellboreTypes: TDictObject[] = [];
  @observable.ref trajectoryTypes: TDictObject[] = [];
  @observable.ref sectionTypes: TDictObject[] = [];
  @observable.ref stratUnitTypes: TDictObject[] = [];
  @observable.ref reservoirTypes: TDictObject[] = [];
  @observable.ref runTypes: TDictObject[] = [];
  @observable.ref operationTypes: TDictObject[] = [];
  @observable.ref parameters: TDictObject[] = [];

  @observable private recommendations: Recommendation[] = [];
  @observable deletedRecommendationIds: string[] = [];

  recommendationsMap = observable.map<string, Recommendation>();

  constructor(
    agent: AxiosInstance,
    private readonly userData: TUserData,
    private readonly directoryService: IDirectoriesService,
  ) {
    this.api = new RecommendationsApi(agent);
    makeObservable(this);
  }

  @computed
  get rows(): Recommendation[] {
    return this.recommendations;
  }

  @computed
  get hasUnsavedChanges(): boolean {
    return (
      this.recommendations.some((row) => row.isChanged || row.id == null) || this.deletedRecommendationIds.length > 0
    );
  }

  @flow.bound
  private *fetchRecommendations() {
    this.loading = true;
    this.abortControllers.get('fetchRecommendations')?.abort();
    const ac = new AbortController();
    this.abortControllers.set('fetchRecommendations', ac);

    try {
      const list: TRecommendationResponse[] = yield this.api.fetchList(ac.signal);
      const sortedList = [...list].sort((a, b) => getCreatedAtTime(a) - getCreatedAtTime(b));

      this.recommendations = sortedList.map((d) => new Recommendation(mapResponseToRow(d), this.userData));
      this.recommendationsMap.clear();
      this.recommendationsMap.merge(this.recommendations.map((r) => [r.entityId, r]));
    } catch (e) {
      if (axios.isCancel(e)) return;
      console.error(e);
    } finally {
      this.loading = false;
      this.abortControllers.delete('fetchRecommendations');
    }
  }

  @flow.bound
  private async *fetchDirectories() {
    try {
      const [
        blocks,
        fields,
        pads,
        autosupervisorOperations,
        autosupervisorKPIstates,
        workClassifierStageTypes,
        wellboreTypes,
        trajectoryTypes,
        sectionTypes,
        stratUnitTypes,
        reservoirTypes,
        runTypes,
        operationTypes,
        parameters,
      ] = await this.directoryService.loadObjects([
        'Common_Block',
        'Common_Field',
        'Common_Pad',
        'Common_AutosupervisorOperations',
        'Common_AutosupervisorKPIstates',
        'GOreport_WorkClassifier_Stage',
        'Common_WellboreType',
        'Common_TrajectoryType',
        'Common_SectionType',
        'Common_StratUnitType',
        'Common_Reservoir',
        'Common_RunType',
        'Common_AutosupervisorOperations',
        'Common_Parameter',
      ]);
      yield;

      this.blocks = blocks ?? [];
      this.fields = fields ?? [];
      this.pads = pads ?? [];
      this.autosupervisorOperations = autosupervisorOperations ?? [];
      this.autosupervisorKPIstates = autosupervisorKPIstates ?? [];
      this.workClassifierStageTypes = workClassifierStageTypes ?? [];
      this.wellboreTypes = wellboreTypes ?? [];
      this.trajectoryTypes = trajectoryTypes ?? [];
      this.sectionTypes = sectionTypes ?? [];
      this.stratUnitTypes = stratUnitTypes ?? [];
      this.reservoirTypes = reservoirTypes ?? [];
      this.runTypes = runTypes ?? [];
      this.operationTypes = operationTypes ?? [];
      this.parameters = parameters ?? [];

      this.directoriesLoaded = true;
    } catch (e) {
      yield;
      console.error(e);
    }
  }

  private getDefaultRecommendationData(): TData {
    return {};
  }

  private isDataTypeAllowed(indexFormat: TData['indexFormat'], dataType: TData['dataType']): boolean {
    if (indexFormat == null || dataType == null) {
      return true;
    }

    if (indexFormat === 'LOG_DEPTH') {
      return dataType === 'GENERAL_PARAMETERS' || dataType === 'DRILLING_LOADS';
    }

    return dataType !== 'DRILLING_LOADS';
  }

  private isParamValueAllowed(data: Partial<TData>): boolean {
    if (data.paramValue == null) {
      return true;
    }

    if (data.dataType === 'GENERAL_PARAMETERS') {
      const parameters =
        data.indexFormat === 'LOG_DEPTH' ? depthSortedParameters : timeSortedParametersNoAutoRecognized;

      return typeof data.paramValue === 'string' && parameters.some((parameter) => parameter === data.paramValue);
    }

    if (data.dataType === 'DRILLING_LOADS') {
      return (
        data.indexFormat === 'LOG_DEPTH' &&
        typeof data.paramValue === 'string' &&
        (DRILLING_LOADS_MNEMONICS as readonly string[]).includes(data.paramValue)
      );
    }

    if (data.indexFormat === 'LOG_DEPTH') {
      return false;
    }

    if (data.dataType === 'AUTOSUPERVISOR_OPERATIONS') {
      return (
        typeof data.paramValue === 'number' &&
        this.autosupervisorOperations.some((operation) => operation.data.code === data.paramValue)
      );
    }

    if (data.dataType === 'AUTOSUPERVISOR_KPI_STATES') {
      return (
        typeof data.paramValue === 'number' &&
        this.autosupervisorKPIstates.some((kpi) => kpi.data.code === data.paramValue)
      );
    }

    return false;
  }

  private normalizeRecommendationPatch(baseData: TData, patch: Partial<TData>): Partial<TData> {
    const nextData = { ...baseData, ...patch };
    const normalizedPatch: Partial<TData> = { ...patch };

    if ('indexFormat' in patch) {
      if (!('dataType' in patch)) {
        normalizedPatch.dataType = undefined;
      }

      if (!('paramValue' in patch)) {
        normalizedPatch.paramValue = undefined;
      }
    }

    if ('dataType' in patch && !('paramValue' in patch)) {
      normalizedPatch.paramValue = undefined;
    }

    if (!this.isDataTypeAllowed(nextData.indexFormat, nextData.dataType)) {
      normalizedPatch.dataType = undefined;
      normalizedPatch.paramValue = undefined;
    }

    const effectiveData = { ...nextData, ...normalizedPatch };

    if (!this.isParamValueAllowed(effectiveData)) {
      normalizedPatch.paramValue = undefined;
    }

    return normalizedPatch;
  }

  private createRecommendationDataFromPatch(patch: Partial<TData>): TData {
    const baseData = this.getDefaultRecommendationData();
    const normalizedPatch = this.normalizeRecommendationPatch(baseData, patch);

    return {
      ...baseData,
      ...normalizedPatch,
    };
  }

  addNewRecommendationItems(initialRecordsData?: TData[]): void {
    if (!initialRecordsData) {
      const newItem = new Recommendation(this.getDefaultRecommendationData(), this.userData);
      this.recommendations.push(newItem);
      this.recommendationsMap.set(newItem.entityId, newItem);
      this.eventEmitter.emit('newRecommendations', [newItem]);
      return;
    }

    if (!initialRecordsData.length) return;

    const added: Recommendation[] = [];
    for (const data of initialRecordsData) {
      const newItem = new Recommendation(data, this.userData);
      this.recommendations.push(newItem);
      added.push(newItem);
      this.recommendationsMap.set(newItem.entityId, newItem);
    }
    this.eventEmitter.emit('newRecommendations', added);
  }

  @action.bound
  addNewRecommendationItemPatches(patches: Partial<TData>[]): void {
    this.addNewRecommendationItems(patches.map((patch) => this.createRecommendationDataFromPatch(patch)));
  }

  updateRecommendationItems(data: [string, TData][]): void {
    const updated: Recommendation[] = [];
    for (const [recordId, rowData] of data) {
      const item = this.recommendationsMap.get(recordId);
      if (!item) {
        console.warn(`cannot find recommendation item with id ${recordId}`);
        continue;
      }
      item.updateData(rowData);
      updated.push(item);
    }
    this.eventEmitter.emit('updateRecommendations', updated);
  }

  @action.bound
  applyRecommendationItemPatches(data: [string, Partial<TData>][]): void {
    const updated: Recommendation[] = [];

    for (const [recordId, patch] of data) {
      const item = this.recommendationsMap.get(recordId);
      if (!item) {
        console.warn(`cannot find recommendation item with id ${recordId}`);
        continue;
      }

      const normalizedPatch = this.normalizeRecommendationPatch(item.data, patch);
      item.updateData({ ...item.data, ...normalizedPatch });
      updated.push(item);
    }

    this.eventEmitter.emit('updateRecommendations', updated);
    this.validationService.validateRecommendations(updated);
  }

  @action.bound
  deleteRecommendationItems(ids: string[]) {
    if (!ids.length) return;
    const idsMap = new Map(ids.map((id) => [id, id]));

    for (let i = 0; i < this.recommendations.length; ) {
      const item = this.recommendations[i];
      if (idsMap.has(item.entityId)) {
        this.recommendationsMap.delete(item.entityId);
        this.recommendations.splice(i, 1);
        if (item.id != null) {
          this.deletedRecommendationIds.push(item.id);
        }
        continue;
      }
      i++;
    }
    this.eventEmitter.emit('deleteRecommendations', ids);
  }

  onRecommendationItemValueChange<T extends keyof TRecommendationRowData>(
    recordId: string,
    field: T,
    value: TRecommendationRowData[T] | undefined,
  ): void {
    const item = this.recommendationsMap.get(recordId);
    if (!item) {
      console.error(`cannot find recommendation with id ${recordId}`);
      return;
    }
    const patch = { [field]: value } as Partial<TData>;
    this.applyRecommendationItemPatches([[recordId, patch]]);
  }

  setEditing(value: boolean) {
    this.uiState.setEditing(value);
  }

  @flow.bound
  *discardChanges() {
    this.deletedRecommendationIds = [];
    yield this.fetchRecommendations();
  }

  @action.bound
  saveRecommendations() {
    if (this.validationService.validateRecommendations(this.recommendations)) {
      return Promise.reject(new Error('Validation errors'));
    }

    const createItems: TRecommendationRequest[] = [];
    const updateItems: (TRecommendationRequest & { id: string })[] = [];
    const deleteIds: string[] = this.deletedRecommendationIds;

    for (const r of this.recommendations) {
      const payload = serializeRecommendation(r);
      if (r.id == null) {
        createItems.push(payload);
      } else {
        updateItems.push({ ...payload, id: r.id });
      }
    }

    return this.api
      .save({ create: createItems, update: updateItems, delete: deleteIds })
      .then((res) => {
        runInAction(() => {
          this.deletedRecommendationIds = [];
        });
        return res;
      })
      .then(() => this.fetchRecommendations());
  }

  loadInitialData() {
    return Promise.all([this.fetchRecommendations(), this.fetchDirectories()]);
  }

  init = (): VoidFunction => {
    void this.loadInitialData();
    return () => {
      this.abortControllers.forEach((c) => c.abort());
    };
  };
}
